import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Modal,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@ltvco/refresh-lib/theme';
import {
  useAccount,
  useSession,
  invalidateSession,
  closeSnackbar,
} from '@ltvco/refresh-lib/v1';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { CHATBV_GA4_EVENTS } from 'components/ChatBV/constants';
import { goToLogin } from 'utils/goToLogin';

import { LogoutButton } from './LogoutButton';
import { NavigationSection } from './NavigationSection';
import { ReportUsageSection, type ReportUsage } from './ReportUsageSection';
import { getModalStyles, getContainerBoxStyles } from './styles';
import { UpgradeSection } from './UpgradeSection';
import { UserInfoSection } from './UserInfoSection';

type ProfileModalProps = {
  open: boolean;
  onClose: () => void;
  onAccountClick?: () => void;
  onUpgradePlanClick?: () => void;
  onHelpClick?: () => void;
  onFeedbackClick?: () => void;
  onLogout?: () => void;
  // When provided, the report usage meter renders inside the modal. Callers that
  // don't pass it (e.g. ChatBV) get the modal unchanged.
  reportUsage?: ReportUsage;
};

export const ProfileModal = ({
  open,
  onClose,
  onAccountClick,
  onUpgradePlanClick,
  onHelpClick,
  onFeedbackClick,
  onLogout,
  reportUsage,
}: ProfileModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const { trackEventGA4 } = useContext(AppConfig);
  const {
    links: { FaqLink },
  } = constants;

  const handleAccountClick = () => {
    onClose();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.account_page,
    });
    if (onAccountClick) {
      onAccountClick();
    } else {
      navigate('/chatbv/account');
    }
  };

  const handleHelpClick = () => {
    onClose();
    onHelpClick?.();
  };

  const handleFeedbackClick = () => {
    onClose();
    onFeedbackClick?.();
  };

  const handleUpgradeClick = () => {
    onClose();
    onUpgradePlanClick?.();
  };

  const handleLearnMoreClick = () => {
    window.open(FaqLink, '_blank');
  };

  const handleLogoutClick = async () => {
    onClose();
    if (onLogout) {
      onLogout();
    } else {
      await invalidateSession();
      closeSnackbar();
      goToLogin('https://www.beenverified.com');
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={getModalStyles(isMobile)}>
      <Box sx={getContainerBoxStyles(theme)}>
        <UserInfoSection account={account} />

        {reportUsage && (
          <>
            <Divider sx={{ borderColor: theme.palette.divider }} />
            <ReportUsageSection usage={reportUsage} />
          </>
        )}

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <NavigationSection
          onAccountClick={handleAccountClick}
          onHelpClick={handleHelpClick}
          onFeedbackClick={handleFeedbackClick}
        />

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <UpgradeSection
          onUpgradeClick={handleUpgradeClick}
          onLearnMoreClick={handleLearnMoreClick}
        />

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <LogoutButton onLogout={handleLogoutClick} />
      </Box>
    </Modal>
  );
};
